import { useEffect, useState } from "react";
import type { PersonForLogin, SessionInfo } from "../../shared/types.ts";
import { ApiError, api } from "../api.ts";
import { Banner, Loading } from "../components.tsx";

const PIN_LENGTH = 4;
const PAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

/**
 * Two steps: pick who you are (big name tiles, fetched from the
 * unauthenticated /api/people-for-login), then a PIN pad. Nothing here ever
 * sees a real password — just a 4-digit PIN, auto-submitted on the 4th tap.
 */
export function Login({ onSignedIn }: { onSignedIn: (session: SessionInfo) => void }) {
  const [people, setPeople] = useState<PersonForLogin[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [person, setPerson] = useState<PersonForLogin | null>(null);

  useEffect(() => {
    api
      .peopleForLogin()
      .then(setPeople)
      .catch(() => setLoadError("Couldn't load the family list."));
  }, []);

  if (person) {
    return <PinStep person={person} onBack={() => setPerson(null)} onSignedIn={onSignedIn} />;
  }

  return (
    <div className="login">
      <div className="login-card">
        <h1 className="login-title">
          <span className="login-mark">✓</span> Honeydo
        </h1>
        <p className="muted">Who's this?</p>

        {loadError && <Banner kind="error">{loadError}</Banner>}
        {!people && !loadError && <Loading label="Loading…" />}

        {people && people.length > 0 && (
          <div className="person-picker">
            {people.map((p) => (
              <button key={p.username} className="person-tile" onClick={() => setPerson(p)}>
                {p.name}
              </button>
            ))}
          </div>
        )}
        {people && people.length === 0 && <Banner kind="warn">No one's been seeded yet.</Banner>}
      </div>
    </div>
  );
}

function PinStep({
  person,
  onBack,
  onSignedIn,
}: {
  person: PersonForLogin;
  onBack: () => void;
  onSignedIn: (session: SessionInfo) => void;
}) {
  const [digits, setDigits] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const locked = lockedUntil !== null && secondsLeft > 0;

  // Countdown tick while locked out; clears itself when time's up.
  useEffect(() => {
    if (lockedUntil === null) return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        setLockedUntil(null);
        setMessage(null);
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [lockedUntil]);

  // Shake is a one-shot animation; clear the class once it's played.
  useEffect(() => {
    if (!shake) return;
    const id = setTimeout(() => setShake(false), 400);
    return () => clearTimeout(id);
  }, [shake]);

  async function submit(pin: string) {
    setBusy(true);
    try {
      const session = await api.login(person.username, pin);
      onSignedIn(session);
    } catch (err) {
      setDigits("");
      if (err instanceof ApiError && err.status === 429) {
        const retryAfter = err.retryAfter ?? 30;
        setLockedUntil(Date.now() + retryAfter * 1000);
        setSecondsLeft(retryAfter);
        setMessage("Too many tries.");
      } else {
        setShake(true);
        setMessage("Wrong PIN, try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  function press(key: string) {
    if (busy || locked || key === "") return;
    if (key === "⌫") {
      setDigits((d) => d.slice(0, -1));
      return;
    }
    setMessage(null);
    setDigits((d) => {
      const next = (d + key).slice(0, PIN_LENGTH);
      if (next.length === PIN_LENGTH) void submit(next);
      return next;
    });
  }

  return (
    <div className="login">
      <div className="login-card">
        <button className="link pin-back" onClick={onBack} disabled={busy}>
          ← Not {person.name}?
        </button>
        <h1 className="login-title">Hi, {person.name}</h1>
        <p className="muted">Enter your 4-digit PIN</p>

        {message && (
          <Banner kind={locked ? "warn" : "error"}>
            {locked ? `${message} Try again in ${secondsLeft}s.` : message}
          </Banner>
        )}

        <div className={`pin-dots ${shake ? "pin-shake" : ""}`} aria-hidden="true">
          {Array.from({ length: PIN_LENGTH }, (_, i) => (
            <span key={i} className={`pin-dot ${i < digits.length ? "pin-dot-filled" : ""}`} />
          ))}
        </div>

        <div className="pin-pad">
          {PAD_KEYS.map((key, i) =>
            key === "" ? (
              <span key={`gap-${i}`} className="pin-key pin-key-ghost" />
            ) : (
              <button
                key={key}
                type="button"
                className={`pin-key ${key === "⌫" ? "pin-key-fn" : ""}`}
                onClick={() => press(key)}
                disabled={busy || locked || (key === "⌫" && digits.length === 0)}
                aria-label={key === "⌫" ? "Backspace" : key}
              >
                {key}
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
